// src/pages/SettingsPage/components/AddPaymentMethodModal.tsx
import { useAuth0 } from "@auth0/auth0-react";
import { Box, Button, Modal, Paper, Typography, useTheme } from "@mui/material";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StripeError } from "@stripe/stripe-js";
import React, { useState } from "react";
import {
  useFetchPaymentMethods,
  useFetchStripeSubscription,
} from "../../../hook/settings";
import {
  createSetupIntent,
  setDefaultPaymentMethod,
} from "../../../api/payment";

export const AddPaymentMethodModal: React.FC<{
  modalOpen: boolean;
  setModalOpen: (modalOpen: boolean) => any;
}> = ({ modalOpen, setModalOpen }) => {
  const theme = useTheme();
  const elements = useElements()!;
  const stripe = useStripe()!;
  const [error, setError] = useState<StripeError | undefined>(undefined);
  const { user } = useAuth0();
  const email = user!.email!;
  const fetchPaymentMethods = useFetchPaymentMethods();
  const fetchSubscription = useFetchStripeSubscription();

  const onSubmit = async () => {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError({ message: "Card element not found" } as StripeError); // Basic error
      return;
    }

    try {
      const { "client-secret": clientSecret } = await createSetupIntent({
        email,
      });

      const { setupIntent, error: confirmError } =
        await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              email,
            },
          },
        });

      if (confirmError) {
        setError(confirmError);
        return;
      }

      if (setupIntent?.status === "succeeded") {
        await setDefaultPaymentMethod({
          email,
          paymentMethod: setupIntent.payment_method as string,
        });
        await fetchPaymentMethods(); // Wait for fetches to complete
        await fetchSubscription();
        setError(undefined);
        setModalOpen(false);
        cardElement.clear(); // Clear card details on success
      } else {
        setError({
          message: `Setup intent status: ${setupIntent?.status}`,
        } as StripeError);
      }
    } catch (apiError: any) {
      setError({
        message: apiError?.message || "An unexpected error occurred.",
      } as StripeError);
    }
  };

  return (
    <Modal open={modalOpen}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <Paper
          elevation={7}
          sx={{
            padding: "2rem",
            width: "40rem",
            gap: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h6">Add Payment Method</Typography>
          <Paper
            elevation={3} // Reduced elevation for inner paper
            sx={{
              padding: "1rem",
              border: `1px solid ${theme.palette.divider}`, // Add border
              // backgroundColor: theme.palette.grey["100"], // Lighter background
            }}
          >
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: theme.palette.text.primary, // Use theme text color
                    "::placeholder": {
                      color: theme.palette.text.secondary, // Use theme placeholder color
                    },
                  },
                  invalid: {
                    color: theme.palette.error.main, // Use theme error color
                  },
                },
              }}
            />
          </Paper>
          {error && (
            <Typography color={theme.palette.error.dark} variant="body2">
              {" "}
              {/* Use variant body2 */}
              {error.message}
            </Typography>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            {" "}
            {/* Align buttons */}
            <Button onClick={() => setModalOpen(false)} color="secondary">
              Cancel
            </Button>
            <Button
              onClick={onSubmit}
              variant="contained"
              disabled={!stripe || !elements}
            >
              Add
            </Button>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};
